import { motion } from 'framer-motion';
import { Crown, Sparkles, MessageCircle, NotebookPen } from 'lucide-react';
import { brand } from '@iheart/shared';
import { cn } from '@iheart/ui';
import { useTotalChatUnread } from '../../lib/chat';
import { useAllMyTasks } from '../../lib/projects';
import { useChatWidgetStore } from './ChatWidgetStore';
import { useDominaBriefStore } from './DominaBriefStore';

/**
 * The floating launcher: three distinct bubbles so each destination is
 * obvious at a glance —
 *   • To-do (dark grey)  → your task brief
 *   • Workspace chats (black) → teammate chats
 *   • Domina AI (pink→blue gradient + sparkles) → the AI assistant
 * Chat + brief panels stay mutually exclusive (opening one closes the other).
 */
export function ChatBubble() {
  const { isOpen, view, close, openChats, openDomina } = useChatWidgetStore();
  const { isExpanded, expand, collapse } = useDominaBriefStore();
  const chatUnread = useTotalChatUnread();
  const totalUnread = chatUnread.data?.unread ?? 0;

  const tasks = useAllMyTasks();
  const outstanding = (tasks.data ?? []).filter((t) => !t.verifiedAt && t.status !== 'DONE').length;

  const chatsOpen = isOpen && (view === 'lobby' || view === 'thread' || view === 'new-chat');
  const dominaOpen = isOpen && view === 'domina';

  const toggleTodo = () => {
    if (isExpanded) collapse();
    else {
      close();
      expand();
    }
  };
  const toggleChats = () => {
    if (chatsOpen) close();
    else {
      collapse();
      openChats();
    }
  };
  const toggleDomina = () => {
    if (dominaOpen) close();
    else {
      collapse();
      openDomina();
    }
  };

  return (
    <div
      className={cn(
        'fixed z-40 flex items-center gap-2.5',
        // Mobile: centred row sitting in its own strip directly above the bottom
        // stack (menu + pinned footer). --mob-bottom is the measured height of
        // that stack; +0.625rem centres the 44px buttons in the 64px strip.
        // NOTE: underscores become spaces — CSS calc() REQUIRES spaces around "+".
        'bottom-[calc(var(--mob-bottom,7rem)_+_0.625rem)] left-1/2 -translate-x-1/2',
        // md+: a dedicated right-aligned row just ABOVE the fixed footer bar
        // (h-14 = 3.5rem), so the footer menu underneath stays fully visible.
        'md:bottom-[4.25rem] md:left-auto md:right-4 md:translate-x-0',
        // On mobile the row hides behind an open panel; on md+ it stays docked
        // above the footer so each bubble keeps toggling its own panel.
        (isOpen || isExpanded) && 'hidden md:flex',
      )}
    >
      {/* To-do — black */}
      <div className="group relative">
        <motion.button
          type="button"
          onClick={toggleTodo}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          transition={brand.motion.spring}
          className="relative flex size-11 items-center justify-center rounded-full bg-neutral-900 text-white shadow-lg shadow-neutral-900/30"
          aria-label="Your tasks"
        >
          <NotebookPen className="size-5" />
          {outstanding > 0 && (
            <span className="absolute -right-1 -top-1 flex size-5 min-w-5 items-center justify-center rounded-full bg-white text-[10px] font-bold text-pink-600 shadow ring-2 ring-pink-500">
              {outstanding > 99 ? '99+' : outstanding}
            </span>
          )}
        </motion.button>
        <BubbleTooltip label="Your tasks" />
      </div>

      {/* Workspace chats — black */}
      <div className="group relative">
        <motion.button
          type="button"
          onClick={toggleChats}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          transition={brand.motion.spring}
          className="relative flex size-11 items-center justify-center rounded-full bg-neutral-900 text-white shadow-lg shadow-neutral-900/30"
          aria-label="Workspace chats"
        >
          <MessageCircle className="size-5" />
          {totalUnread > 0 && (
            <span className="absolute -right-1 -top-1 flex size-5 min-w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
              {totalUnread > 99 ? '99+' : totalUnread}
            </span>
          )}
        </motion.button>
        <BubbleTooltip label="Workspace chats" />
      </div>

      {/* Domina AI — pink→blue gradient with AI sparkles */}
      <div className="group relative">
        <motion.button
          type="button"
          onClick={toggleDomina}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.94 }}
          transition={brand.motion.spring}
          className="relative flex size-11 items-center justify-center rounded-full text-white shadow-lg shadow-pink-500/30"
          style={{ backgroundImage: brand.gradient.primary }}
          aria-label="Ask Domina AI"
        >
          <Crown className="size-5" />
          <Sparkles className="absolute right-1 top-1 size-2.5" />
          <Sparkles className="pointer-events-none absolute -left-1.5 -top-1.5 size-3 text-pink-300 animate-pulse" />
          <Sparkles className="pointer-events-none absolute -bottom-1.5 -right-1 size-2.5 text-indigo-300 animate-pulse [animation-delay:400ms]" />
        </motion.button>
        <BubbleTooltip label="Domina AI" />
      </div>
    </div>
  );
}

/** A small name-label that fades in above a bubble on hover. */
function BubbleTooltip({ label }: { label: string }) {
  return (
    <span className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100">
      {label}
    </span>
  );
}
